import React, { useEffect, useState, useRef } from "react";
import classNames from "classnames";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Hidden from "@material-ui/core/Hidden";
import Poppers from "@material-ui/core/Popper";
import Divider from "@material-ui/core/Divider";
import Person from "@material-ui/icons/Person";
import Button from "../../Components/CustomButtons/Button.js";
import styles from "../../assets/jss/material-dashboard-react/components/headerLinksStyle.js";
import { useHistory } from 'react-router-dom';
import { Notifications } from "@material-ui/icons";
import { connect, useDispatch, useSelector } from "react-redux";
import { PageStatus } from "../../enums";
import { AuthAPI } from "../../API";
import { menuDict } from "Languages/ProfileTranslations.js";
import { languageChangeNavbar, countryChangeNavbar } from "Components/Auth/auth.actions.js";
import { MasterDataAPI } from "../../API";
import PropTypes from "prop-types";

const useStyles = makeStyles(styles);

function AdminNavbarLinks(props) {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openNotification, setOpenNotification] = React.useState(null);
  const [openProfile, setOpenProfile] = React.useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState(PageStatus.None);
  const [errors, setErrors] = useState('');
  const lang = props.language ?? 'en';
  const { userId, role } = useSelector((state) => state.adminUser.adminUser);
  const selectedCountry = useSelector((state) => state.adminUser.adminUser.selectedCountry) || 'ALL';
  const [countries, setCountries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Track newest createdAt seen across polls so we only toast genuine new arrivals.
  // UUID ids aren't sequential; createdAt is monotonic and matches the backend
  // ORDER BY (Notifications.controller.js userNotifications: ORDER BY createdAt DESC).
  const lastSeenAtRef = useRef(null);
  const isFirstPollRef = useRef(true);
  useEffect(() => {
    if (!userId) return undefined;

    const fetchData = async () => {
      try {
        setStatus(PageStatus.Loading);
        const [list, unread] = await Promise.all([
          AuthAPI.userNotifications(userId),
          AuthAPI.notificationUnreadCount(userId),
        ]);
        if (Array.isArray(list)) {
          // Backend returns DESC by createdAt — list[0] is newest.
          if (isFirstPollRef.current) {
            // Seed the high-water mark on first mount; do NOT toast historical rows.
            if (list.length > 0) {
              lastSeenAtRef.current = list[0].createdAt;
            }
            isFirstPollRef.current = false;
          } else {
            const cutoff = lastSeenAtRef.current;
            const fresh = cutoff
              ? list.filter((n) => n && n.createdAt && n.createdAt > cutoff)
              : list;
            if (fresh.length > 0) {
              // Toast oldest-first so popup order matches chronological arrival.
              // Cap at 5 per poll so a backlog doesn't bury the screen.
              const toToast = fresh.slice(0, 5).reverse();
              toToast.forEach((n) => {
                toast.info(n.message, { autoClose: 5000 });
              });
              lastSeenAtRef.current = fresh[0].createdAt;
            }
          }
          setData(list);
        }
        setTotalCount(Number(unread) || 0);
        setStatus(PageStatus.Loaded);
      } catch (error) {
        setStatus(PageStatus.Error);
        setErrors(error && error.message ? error.message : 'Failed to load notifications');
      }
    };

    fetchData();
    // Poll every 60s so bell reflects automated events (quota, IR/DOR, end-date, status)
    const intervalId = setInterval(fetchData, 60000);
    return () => clearInterval(intervalId);
  }, [userId]);

  const ALLOWED_COUNTRIES = ['IN', 'UK', 'JP', 'SG', 'MY', 'PH'];

  useEffect(() => {
    MasterDataAPI.countryList('10000')
      .then((data) => {
        if (Array.isArray(data)) {
          const filtered = data
            .filter((c) => ALLOWED_COUNTRIES.includes(c.shortName))
            .map((c) => ({ name: c.name, shortName: c.shortName }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setCountries(filtered);
        }
      })
      .catch((err) => console.error('Failed to load countries:', err));
  }, []);

  const handleNotificationClick = (event) => {
    if (role === 'panelist') {
      setTotalCount(0);
      history.push("/panelist/Notifications");
    } else {
      if (openNotification?.contains(event.target)) {
        setOpenNotification(null);
      } else {
        setOpenNotification(event.currentTarget);
        // Badge no longer auto-zeroes on bell open — that defeated read-tracking
        // (count dropped to 0 the instant the dropdown opened, before the user
        // had a chance to read). Mark-as-read should be an explicit user action;
        // follow-up will wire a "Mark all as read" button into the dropdown.
      }
    }
  };

  const handleCloseNotification = () => {
    setOpenNotification(null);
  };
  const handleClickProfile = (event) => {
    if (openProfile?.contains(event.target)) {
      setOpenProfile(null);
    } else {
      setOpenProfile(event.currentTarget);
    }
  };

  const handleCloseProfile = () => {
    localStorage.clear();
    window.location.href = '/';
    setOpenProfile(null);
  };

  const handleClickSettings = () => {
    history.push(`/${role}/settings`);
    setOpenProfile(null);
  }

  const handleClickProfiles = () => {
    history.push(`/${role}/profile`);
    setOpenProfile(null);
  }

  return (
    <div>
      <div className={classes.manager}>
        <div className="text-center">
          <select id="country-dropdown" className="text-center" value={selectedCountry}
            onChange={(e) => {
              dispatch(countryChangeNavbar(e.target.value));
            }}
            style={{ marginRight: '8px' }}
          >
            <option value="ALL">All Countries</option>
            {countries.map((c) => (
              <option key={c.shortName} value={c.shortName}>{c.name} ({c.shortName})</option>
            ))}
          </select>
        </div>
      </div>
      <div className={classes.manager}>
        <div className="text-center">
          <select id="language-dropdown" className="text-center" defaultValue={lang}
            onChange={(e) => {
              dispatch(languageChangeNavbar(e.target.value));
            }}
          >
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
          </select>
        </div>
      </div>
      <div className={classes.manager}>
        <Button
          color={window.innerWidth > 959 ? "transparent" : "white"}
          justIcon={window.innerWidth > 959}
          simple={(window.innerWidth <= 959)}
          aria-owns={openNotification ? "notification-menu-list-grow" : null}
          aria-haspopup="true"
          onClick={handleNotificationClick}
          className={classes.buttonLink}
        >
          <Notifications className={classes.icons} />
          {/* Display totalCount fetched from the API */}
          {totalCount > 0 && (
            <span className={classes.notifications}>{totalCount}</span>
          )}
          <Hidden mdUp implementation="css">
            <span onClick={handleCloseNotification} className="bg-transparent text-white">
              <p className={classes.linkText}>
                {menuDict[lang]["Notification"] || "Notification"}
              </p>
            </span>
          </Hidden>
        </Button>
        <Poppers
          open={Boolean(openNotification)}
          anchorEl={openNotification}
          transition
          disablePortal
          className={
            classNames({ [classes.popperClose]: !openNotification }) +
            " " +
            classes.popperNav
          }
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              id="notification-menu-list-grow"
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleCloseNotification}>
                  <MenuList role="menu">
                    {data && data.length > 0 ? data.map((info) => (
                      <MenuItem
                        key={info.id}
                        onClick={handleCloseNotification}
                        className={classes.dropdownItem}
                      >
                        {info.message}
                      </MenuItem>
                    )) : (
                      <MenuItem
                        className={classes.dropdownItem}
                        style={{
                          opacity: 0.6,
                          fontStyle: 'italic',
                          justifyContent: 'center',
                          minWidth: '200px',
                          pointerEvents: 'none',
                        }}
                      >
                        {menuDict[lang]["No notifications"] || "You're all caught up!"}
                      </MenuItem>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Poppers>
      </div>
      <div className={classes.manager}>
        <Button
          color={window.innerWidth > 959 ? "transparent" : "white"}
          justIcon={window.innerWidth > 959}
          simple={(window.innerWidth <= 959)}
          aria-owns={openProfile ? "profile-menu-list-grow" : null}
          aria-haspopup="true"
          onClick={handleClickProfile}
          className={classes.buttonLink}
        >
          <Person className={classes.icons} />
          <Hidden mdUp implementation="css">
            <p className={classes.linkText}>{menuDict[lang]["Profile"] || "Profile"}</p>
          </Hidden>
        </Button>
        <Poppers
          open={Boolean(openProfile)}
          anchorEl={openProfile}
          transition
          disablePortal
          className={
            classNames({ [classes.popperClose]: !openProfile }) +
            " " +
            classes.popperNav
          }
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              id="profile-menu-list-grow"
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener>
                  <MenuList role="menu">
                    <MenuItem
                      onClick={() => handleClickProfiles()}
                      className={classes.dropdownItem}
                    >
                      {menuDict[lang]["Profile"] || "Profile"}
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleClickSettings()}
                      className={classes.dropdownItem}
                    >
                      {menuDict[lang]["Settings"] || "Settings"}
                    </MenuItem>
                    <Divider light />
                    <MenuItem
                      onClick={() => handleCloseProfile()}
                      className={classes.dropdownItem}
                    >
                      {menuDict[lang]["Logout"] || "Logout"}
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>

            </Grow>
          )}
        </Poppers>
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    userId: state.adminUser.adminUser.userId,
    role: state.adminUser.adminUser.role,
    language: state.adminUser.adminUser.language,
    selectedCountry: state.adminUser.adminUser.selectedCountry,
  };
}

AdminNavbarLinks.propTypes = {
  language: PropTypes.string,
  selectedCountry: PropTypes.string,
}
export default connect(mapStateToProps, {})(AdminNavbarLinks);
